import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search as SearchIcon, Filter } from "lucide-react";
import { api } from "../../lib/api";
import { EventCardGrid } from "../../components/EventCard";
import { SearchableSelect } from "../../components/SearchableSelect";
import { CustomDatePicker } from "../../components/CustomPickers";

export default function SearchPage() {
    const { t } = useTranslation();
    const [countries, setCountries] = useState([]);
    const [categories, setCategories] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    const [country, setCountry] = useState("");
    const [category, setCategory] = useState("");
    const [state, setState] = useState("");
    const [city, setCity] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [kw, setKw] = useState("");

    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            const [c, cat] = await Promise.all([
                api.get("/api/countries"),
                api.get("/api/categories"),
            ]);
            setCountries(c.data || []);
            setCategories(cat.data || []);
        })();
    }, []);

    useEffect(() => {
        (async () => {
            if (country) {
                const { data } = await api.get("/api/states", { params: { country_id: country } });
                setStates(data || []);
            } else {
                const { data } = await api.get("/api/states");
                setStates(data || []);
            }
            setState("");
            setCity("");
            setCities([]);
        })();
    }, [country]);

    useEffect(() => {
        (async () => {
            if (state) {
                const { data } = await api.get("/api/cities", { params: { state_id: state } });
                setCities(data || []);
            } else {
                setCities([]);
            }
            setCity("");
        })();
    }, [state]);

    const runSearch = async (e) => {
        if (e) e.preventDefault();
        setLoading(true);
        try {
            const params = {};
            if (country) params.country_id = country;
            if (category) params.category_id = category;
            if (state) params.state_id = state;
            if (city) params.city_id = city;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (kw) params.q = kw;
            const { data } = await api.get("/api/events/search", { params });
            setResults(data || []);
        } finally {
            setLoading(false);
        }
    };

    // Auto-search on initial mount
    useEffect(() => {
        runSearch();
        // eslint-disable-next-line
    }, []);

    return (
        <div data-testid="search-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex items-center gap-3 mb-8">
                <div className="p-2.5 rounded-xl bg-muted">
                    <SearchIcon className="h-6 w-6" />
                </div>
                <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
                    {t("search.title")}
                </h1>
            </div>

            <form
                onSubmit={runSearch}
                className="bento-card p-5 md:p-6 mb-10 space-y-4"
                data-testid="search-form"
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Field label={t("search.country")}>
                        <SearchableSelect
                            testId="filter-country"
                            options={[{ value: "", label: t("search.all_countries") }, ...countries.map((c) => ({ value: c.id, label: c.name }))]}
                            value={country}
                            onValueChange={setCountry}
                            placeholder={t("search.all_countries")}
                            searchPlaceholder={t("common.search")}
                            emptyText={t("common.not_found")}
                        />
                    </Field>
                    <Field label={t("search.category")}>
                        <SearchableSelect
                            testId="filter-category"
                            options={[{ value: "", label: t("search.all_categories") }, ...categories.map((c) => ({ value: c.id, label: c.name }))]}
                            value={category}
                            onValueChange={setCategory}
                            placeholder={t("search.all_categories")}
                            searchPlaceholder={t("common.search")}
                            emptyText={t("common.not_found")}
                        />
                    </Field>
                    <Field label={t("search.state")}>
                        <SearchableSelect
                            testId="filter-state"
                            options={[{ value: "", label: t("search.all_states") }, ...states.map((c) => ({ value: c.id, label: c.name }))]}
                            value={state}
                            onValueChange={setState}
                            placeholder={t("search.all_states")}
                            searchPlaceholder={t("common.search")}
                            emptyText={t("common.not_found")}
                        />
                    </Field>
                    <Field label={t("search.city")}>
                        <SearchableSelect
                            testId="filter-city"
                            options={[{ value: "", label: t("search.all_cities") }, ...cities.map((c) => ({ value: c.id, label: c.name }))]}
                            value={city}
                            onValueChange={setCity}
                            placeholder={t("search.all_cities")}
                            searchPlaceholder={t("common.search")}
                            emptyText={t("common.not_found")}
                            disabled={!state}
                        />
                    </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Field label={t("search.date_from")}>
                        <CustomDatePicker
                            testId="filter-date-from"
                            value={dateFrom}
                            onChange={setDateFrom}
                            placeholder={t("search.date_from")}
                        />
                    </Field>
                    <Field label={t("search.date_to")}>
                        <CustomDatePicker
                            testId="filter-date-to"
                            value={dateTo}
                            onChange={setDateTo}
                            placeholder={t("search.date_to")}
                        />
                    </Field>
                    <Field label={t("search.keyword")}>
                        <input
                            data-testid="filter-keyword"
                            type="text"
                            value={kw}
                            onChange={(e) => setKw(e.target.value)}
                            className="input-modern"
                            placeholder={t("search.keyword")}
                        />
                    </Field>
                </div>

                <div className="flex justify-end pt-2">
                    <button
                        type="submit"
                        data-testid="filter-submit"
                        className="btn-primary flex items-center gap-2"
                    >
                        <Filter className="h-4 w-4" />
                        {t("search.search")}
                    </button>
                </div>
            </form>

            <div className="mb-5 flex items-center justify-between">
                <p data-testid="search-results-count" className="text-sm text-muted-foreground font-semibold">
                    {t("search.results", { count: results.length })}
                </p>
            </div>

            {loading ? (
                <div className="text-center py-20 text-muted-foreground">{t("common.loading")}</div>
            ) : results.length ? (
                <EventCardGrid events={results} />
            ) : (
                <div className="rounded-2xl border-2 border-dashed border-border p-12 text-center text-muted-foreground font-medium">
                    {t("search.no_results")}
                </div>
            )}
        </div>
    );
}

const Field = ({ label, children }) => (
    <div>
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-1.5">
            {label}
        </label>
        {children}
    </div>
);
